import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import type { Tables, Enums } from "@/integrations/supabase/types";

type Attribute = Tables<"attributes">;
type AttributeType = Enums<"attribute_type">;

const attributeTypeLabels: Record<AttributeType, string> = {
  text: "Teksti",
  number: "Numero",
  date: "Päivämäärä",
  selection: "Valinta",
  coordinates: "Koordinaatit",
};

const formSchema = z.object({
  name: z.string().min(1, "Nimi on pakollinen").max(50),
  label: z.string().min(1, "Label on pakollinen").max(100),
  attribute_type: z.enum(["text", "number", "date", "selection", "coordinates"]),
  is_required: z.boolean(),
  options: z.array(z.string()).optional(),
  validation_min: z.string().optional(),
  validation_max: z.string().optional(),
  validation_pattern: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface AttributeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attribute: Attribute | null;
}

export function AttributeDialog({
  open,
  onOpenChange,
  attribute,
}: AttributeDialogProps) {
  const queryClient = useQueryClient();
  const [selectionOptions, setSelectionOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      label: "",
      attribute_type: "text",
      is_required: false,
      options: [],
      validation_min: "",
      validation_max: "",
      validation_pattern: "",
    },
  });

  const attributeType = form.watch("attribute_type");

  useEffect(() => {
    if (attribute) {
      const validationRules = attribute.validation_rules as Record<string, unknown> | null;
      const rawOptions = attribute.options as (string | Record<string, unknown>)[] | null;
      const options = rawOptions?.map(o =>
        typeof o === 'string' ? o : (typeof o === 'object' && o !== null ? String((o as Record<string, unknown>).label || (o as Record<string, unknown>).value || JSON.stringify(o)) : String(o))
      ) || null;
      
      form.reset({
        name: attribute.name,
        label: attribute.label,
        attribute_type: attribute.attribute_type,
        is_required: attribute.is_required,
        options: options || [],
        validation_min: validationRules?.min?.toString() || "",
        validation_max: validationRules?.max?.toString() || "",
        validation_pattern: validationRules?.pattern?.toString() || "",
      });
      setSelectionOptions(options || []);
    } else {
      form.reset({
        name: "",
        label: "",
        attribute_type: "text",
        is_required: false,
        options: [],
        validation_min: "",
        validation_max: "",
        validation_pattern: "",
      });
      setSelectionOptions([]);
    }
  }, [attribute, form]);

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const validationRules: Record<string, string | number> = {};
      if (data.validation_min) validationRules.min = parseFloat(data.validation_min) || data.validation_min;
      if (data.validation_max) validationRules.max = parseFloat(data.validation_max) || data.validation_max;
      if (data.validation_pattern) validationRules.pattern = data.validation_pattern;

      const payload: {
        name: string;
        label: string;
        attribute_type: AttributeType;
        is_required: boolean;
        options: string[] | null;
        validation_rules: Record<string, string | number> | null;
      } = {
        name: data.name,
        label: data.label,
        attribute_type: data.attribute_type,
        is_required: data.is_required,
        options: data.attribute_type === "selection" ? selectionOptions : null,
        validation_rules: Object.keys(validationRules).length > 0 ? validationRules : null,
      };

      if (attribute) {
        const { error } = await supabase
          .from("attributes")
          .update(payload)
          .eq("id", attribute.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("attributes").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
      toast.success(attribute ? "Attribuutti päivitetty" : "Attribuutti luotu");
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Tallennus epäonnistui");
    },
  });

  const handleAddOption = () => {
    if (newOption.trim() && !selectionOptions.includes(newOption.trim())) {
      setSelectionOptions([...selectionOptions, newOption.trim()]);
      setNewOption("");
    }
  };

  const handleRemoveOption = (option: string) => {
    setSelectionOptions(selectionOptions.filter((o) => o !== option));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {attribute ? "Muokkaa attribuuttia" : "Uusi attribuutti"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tekninen nimi</FormLabel>
                  <FormControl>
                    <Input placeholder="esim. text_content" {...field} />
                  </FormControl>
                  <FormDescription>
                    Käytetään koodissa, ei välilyöntejä
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Näyttönimi</FormLabel>
                  <FormControl>
                    <Input placeholder="esim. Tekstin sisältö" {...field} />
                  </FormControl>
                  <FormDescription>
                    Näytetään käyttöliittymässä
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="attribute_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tyyppi</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(Object.keys(attributeTypeLabels) as AttributeType[]).map(
                        (type) => (
                          <SelectItem key={type} value={type}>
                            {attributeTypeLabels[type]}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_required"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel className="text-base">Pakollinen</FormLabel>
                    <FormDescription>
                      Arvo on annettava tilauksen yhteydessä
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Selection options */}
            {attributeType === "selection" && (
              <div className="space-y-3">
                <FormLabel>Valintavaihtoehdot</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="Lisää vaihtoehto"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={handleAddOption}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {selectionOptions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectionOptions.map((option) => (
                      <div
                        key={option}
                        className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-sm"
                      >
                        <span>{option}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(option)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Validation rules for number type */}
            {attributeType === "number" && (
              <div className="space-y-3">
                <FormLabel>Validointisäännöt</FormLabel>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="validation_min"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Minimi</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="esim. 0" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="validation_max"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Maksimi</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="esim. 1000" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Validation rules for text type */}
            {attributeType === "text" && (
              <div className="space-y-3">
                <FormLabel>Validointisäännöt</FormLabel>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="validation_min"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Min. pituus</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="esim. 1" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="validation_max"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Max. pituus</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="esim. 500" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="validation_pattern"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs">Regex-kaava (valinnainen)</FormLabel>
                      <FormControl>
                        <Input placeholder="esim. ^[A-Z].*" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Peruuta
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Tallennetaan..." : "Tallenna"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
